/* eslint-disable @next/next/no-img-element */
import React, { FC, useRef, useState } from 'react'
import styles from './LineItem.module.scss'
import { Button } from 'components/Buttons/Button'
import { Button as NewButton } from 'components/WebEv/Buttons'
import { PriceManager, SubscriptionKeys } from 'prices'
import { Currency } from 'prices/types'
import { amCloseSelectPlanModal, amOpenSelectPlanModal, trackSelectMembershipPlan } from 'events/amplitude'
import { useClickOutsideModal } from 'hooks/useClickOutsideModal'
import { IconMap } from 'components/_const_assets/icons'
import cx from 'classnames'
import { HP } from 'components/basic/HP'
import FocusTrapWrapper from 'components/FocusTrapWrapper'
import ReactDOM from 'react-dom'
import { ProductModelSelect } from 'components/ProductModelSelect'
import { MembershipProducts, SupplementProducts } from 'products/allProducts'

export interface LineItemProps {
	id: string
	name: string
	price: number
	hidePrice: boolean
	currency: Currency
	quantity: number
	variantId: number | string

	comparePrice: number
	variant?: string
	description?: string[]
	showVAT?: boolean
	onRemove: () => void
	increaseQuantity: () => void
	decreaseQuantity: () => void

	isMembership: boolean

	setModalVisibility: (b: boolean) => void

	currentMembership: number
	swapPlan: (p: any) => void
}

export const LineItem: FC<LineItemProps> = (props) => {
	const [modalVisible, setModalVisible] = useState(false)
	const pageWrapper = useRef(null)

	const showModal = () => {
		setModalVisible(true)
		props.setModalVisibility(true)
		amOpenSelectPlanModal()
	}

	const hideModal = () => {
		setModalVisible(false)
		props.setModalVisibility(false)
		amCloseSelectPlanModal()
	}

	useClickOutsideModal(pageWrapper, hideModal, false)

	let removeText = 'Remove'

	if (props.isMembership) {
		removeText = 'Change'
	}

	const comparePrice = props.comparePrice

	const savings =
		props.variantId === SupplementProducts.SleepElixir.variants.monthly.id || props.variantId === SupplementProducts.SleepElixir.variants.onetime.id
			? 'Save 25%'
			: `${PriceManager.formatPriceToCurrencyNoDecimal((comparePrice - props.price) * props.quantity, props.currency)} off`

	const isSavings = comparePrice - props.price > 0

	const showLower = (props.description && !!props.description.length) || !!removeText

	const _name = props.name

	const isABMembershipProduct =
		props.variantId === MembershipProducts.EightsleepProMembership.variants.standard.id ||
		props.variantId === MembershipProducts.EightsleepPlusMembership.variants.standard.id ||
		props.variantId === MembershipProducts.AutopilotStandard.variants.standard.id ||
		props.variantId === MembershipProducts.AutopilotEnhanced.variants.standard.id

	return (
		<>
			<div className={styles.container}>
				<div className={styles.upper}>
					<div className={styles.upper_left}>
						<p className={styles.name}>{_name}</p>
						{props.variant ? <p className={styles.variant}>{props.variant}</p> : null}
					</div>
					<div className={styles.upper_right}>
						<p className={styles.price}>
							{isSavings ? <span className={styles.compare}>{PriceManager.formatPriceToCurrencyNoDecimal(comparePrice * props.quantity, props.currency)}</span> : null}
							{!props.hidePrice && PriceManager.formatPriceToCurrencyNoDecimal(props.price * props.quantity, props.currency)}
						</p>
						{savings && isSavings ? <p className={styles.savings}>{savings}</p> : null}
						{props.showVAT ? <p className={styles.vat}>(Includes VAT and delivery)</p> : null}
					</div>
				</div>
				{showLower ? (
					<div className={styles.lower}>
						{props.description
							? props.description.map((paragraph, i) => (
									<p
										className={styles.description}
										key={i}
										dangerouslySetInnerHTML={{ __html: paragraph }}
									/>
							  ))
							: null}

						{props.isMembership ? (
							isABMembershipProduct && (
								<Button
									onClick={() => showModal()}
									id={`remove-button-for-${props.name.toLowerCase().replace(' ', '-')}`}
									className={styles.remove}
								>
									{removeText}
								</Button>
							)
						) : (
							<div className={styles.qty}>
								<Button
									onClick={() => (props.quantity === 1 ? props.onRemove() : props.decreaseQuantity())}
									id={`remove-button-for-${props.name.toLowerCase().replace(' ', '-')}`}
									className={styles.qty_delete}
								>
									{props.quantity === 1 ? (
										<img
											src={IconMap.trash}
											alt="Trash icon"
										/>
									) : (
										<>-</>
									)}
								</Button>
								<span>{props.quantity}</span>
								<Button
									onClick={() => props.increaseQuantity()}
									id={`increase-button-for-${props.name.toLowerCase().replace(' ', '-')}`}
									className={styles.qty_increase}
								>
									+
								</Button>
							</div>
						)}
					</div>
				) : null}
			</div>
			<MembershipModal
				visible={modalVisible}
				pageWrapper={pageWrapper}
				hideModal={hideModal}
				currentMembership={props.currentMembership}
				currency={props.currency}
				outerProps={props}
			/>
		</>
	)
}

const MembershipModal = (props: { visible: boolean; pageWrapper: any; hideModal: () => void; currentMembership: number; currency: Currency; outerProps: any }) => {
	const { visible, pageWrapper, hideModal, currentMembership, currency, outerProps } = props
	const [selectedMembership, setSelectedMembership] = useState(currentMembership)

	const getMembershipSubtitleText = (susbcriptionId: SubscriptionKeys) => {
		return `${PriceManager.getCurrencySubscriptionPrice(susbcriptionId, currency)}/mo`
	}

	const memberships = [
		{
			title: 'Standard',
			subtitle: `${getMembershipSubtitleText('8-pro-membership')}`,
			subtitle2: `(billed annually, plus applicable sales tax)`,
			description: `Automatic temperature, vibration & thermal alarm, sleep & health reports`,
			titleBadge: '',
			id: 'pro_membership_1',
			variantId: MembershipProducts.AutopilotStandard.variants.standard.id,
			sellingPlanId: MembershipProducts.AutopilotStandard.sellingPlanId,
		},
		{
			title: 'Enhanced',
			subtitle: `${getMembershipSubtitleText('8-plus-membership')}`,
			subtitle2: `(billed annually, plus applicable sales tax)`,
			description: `Automatic temperature, vibration & thermal alarm, sleep & health reports, extended warranty`,
			titleBadge: 'Most Popular',
			id: 'premium_membership_1',
			variantId: MembershipProducts.AutopilotEnhanced.variants.standard.id,
			sellingPlanId: MembershipProducts.AutopilotEnhanced.sellingPlanId,
		},
	]

	const updateMembership = async () => {
		const plan = memberships[selectedMembership]
		outerProps.swapPlan(plan)
	}

	if (visible) {
		return ReactDOM.createPortal(
			<FocusTrapWrapper active={true}>
				<div className={'modal_container'}>
					<div
						className={styles.modal_content_container}
						ref={pageWrapper}
					>
						<button
							aria-label="Close modal button"
							className={styles.close_button}
							onClick={hideModal}
						>
							<img
								src={IconMap.close}
								alt="close icon"
							/>
						</button>
						<div className={cx(styles.content)}>
							<HP
								title={'Change Autopilot'}
								type="h2"
								titleClass={cx('h5', styles.modal_h)}
								paragraphs={[]}
							/>
							<ProductModelSelect
								labels={memberships}
								legendLabel={'Select membership type'}
								selectedModel={selectedMembership}
								onCheck={(i) => {
									setSelectedMembership(i)
									const plan = memberships[i]
									trackSelectMembershipPlan(plan.title)
								}}
								className={styles.membership_fieldset}
							/>
							<NewButton.Primary
								onClick={updateMembership}
								className={styles.atc_button}
								id="update-membership-button"
								// loading={props.cartLoading && !props.usingOneClick}
								// disabled={props.cartLoading}
							>
								Update
							</NewButton.Primary>
						</div>
					</div>
				</div>
			</FocusTrapWrapper>,
			document.getElementById('email_cap_root') || document.body
		)
	}

	return null
}
